import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useQuoteChangeMutations,
  type CreateQuoteChangeRequestDto,
  type QuoteItemInputDto,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const DECIMAL_REGEX = /^\d{1,18}(\.\d{1,6})?$/;

interface ItemDraft {
  category: string;
  name: string;
  description: string;
  quantity: string;
  unit: string;
  listUnitPrice: string;
  discountAmount: string;
  finalUnitPrice: string;
  giftCost: string;
  marginAmount: string;
}

const EMPTY_ITEM: ItemDraft = {
  category: '',
  name: '',
  description: '',
  quantity: '1',
  unit: 'set',
  listUnitPrice: '0',
  discountAmount: '0',
  finalUnitPrice: '0',
  giftCost: '0',
  marginAmount: '0',
};

function useSearchParam(name: string): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

export default function QuoteChangeCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canCreate = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CHANGE_APPROVE,
  );
  const { create, loading, error } = useQuoteChangeMutations();
  const initialContractId = useSearchParam('contractId');

  const [contractId, setContractId] = useState(initialContractId);
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([{ ...EMPTY_ITEM }]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const valid = useMemo(() => {
    if (!contractId.trim() || !title.trim() || !reason.trim()) return false;
    if (items.length === 0) return false;
    return items.every(
      (item) =>
        item.category.trim().length > 0 &&
        item.name.trim().length > 0 &&
        item.unit.trim().length > 0 &&
        DECIMAL_REGEX.test(item.quantity) &&
        DECIMAL_REGEX.test(item.listUnitPrice) &&
        DECIMAL_REGEX.test(item.discountAmount) &&
        DECIMAL_REGEX.test(item.finalUnitPrice) &&
        DECIMAL_REGEX.test(item.giftCost),
    );
  }, [contractId, title, reason, items]);

  if (!canCreate) return <AccessDenied resource="报价变更" />;

  const updateItem = (index: number, patch: Partial<ItemDraft>) => {
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!valid) {
      setSubmitError('请检查必填项与金额格式');
      return;
    }
    const dto: CreateQuoteChangeRequestDto = {
      contractId: contractId.trim(),
      title: title.trim(),
      reason: reason.trim(),
      proposedItems: items.map<QuoteItemInputDto>((item) => ({
        category: item.category.trim(),
        name: item.name.trim(),
        description: item.description.trim() || undefined,
        quantity: item.quantity,
        unit: item.unit.trim(),
        listUnitPrice: item.listUnitPrice,
        discountAmount: item.discountAmount,
        finalUnitPrice: item.finalUnitPrice,
        giftCost: item.giftCost,
        marginAmount: item.marginAmount,
      })),
    };
    const created = await create(dto);
    if (created) {
      navigate(`/quote-contract/change-requests/${created.id}`);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">发起报价变更</h1>
        <p className="text-sm text-muted-foreground">
          已签署 / 已生效 / 变更中合同可以发起变更工单。原合同记录与原报价保留为历史。
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="contractId">合同 ID</Label>
            <Input
              id="contractId"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              placeholder="contract_..."
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="title">变更标题</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="reason">变更原因</Label>
            <textarea
              id="reason"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">变更明细</h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setItems((current) => [...current, { ...EMPTY_ITEM }])
            }
          >
            添加一行
          </Button>
        </div>
        {items.map((item, index) => (
          <Card key={index} className="space-y-3 p-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <div className="space-y-1">
                <Label>分类</Label>
                <Input
                  value={item.category}
                  onChange={(e) =>
                    updateItem(index, { category: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>名称</Label>
                <Input
                  value={item.name}
                  onChange={(e) => updateItem(index, { name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>单位</Label>
                <Input
                  value={item.unit}
                  onChange={(e) => updateItem(index, { unit: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>数量</Label>
                <Input
                  value={item.quantity}
                  onChange={(e) =>
                    updateItem(index, { quantity: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>定价单价</Label>
                <Input
                  value={item.listUnitPrice}
                  onChange={(e) =>
                    updateItem(index, { listUnitPrice: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>折扣</Label>
                <Input
                  value={item.discountAmount}
                  onChange={(e) =>
                    updateItem(index, { discountAmount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>实际单价</Label>
                <Input
                  value={item.finalUnitPrice}
                  onChange={(e) =>
                    updateItem(index, { finalUnitPrice: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>赠送成本</Label>
                <Input
                  value={item.giftCost}
                  onChange={(e) =>
                    updateItem(index, { giftCost: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>毛利</Label>
                <Input
                  value={item.marginAmount}
                  onChange={(e) =>
                    updateItem(index, { marginAmount: e.target.value })
                  }
                />
              </div>
            </div>
          </Card>
        ))}
      </Card>

      {(submitError || error) && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {submitError ?? error?.message}
        </Card>
      )}

      <div className="flex gap-3">
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? '保存中…' : '保存草稿'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate('/quote-contract/change-requests')}
        >
          返回列表
        </Button>
      </div>
    </div>
  );
}
